import { ImsObject } from 'app/shared/collections/ims_object';
import { AssetTemplateResource } from 'app/shared/resources';
import { AssetTemplateIntegration } from './asset_template_integration';
import { Tag } from '../tag';
import * as _ from 'lodash';
import { IpDeviceTemplate } from './ip_device_template';
import { Moment } from 'moment-timezone';
import { isValidRequiredString } from 'app/form_config/components/string_input';
import { AssetType } from 'app/shared/collections/asset_type';
import { Asset } from 'app/shared/collections/asset';

export const IMS_TYPE__ASSET_TEMPLATE = 'asset_template';

interface IAssetTemplateResource {
    site_id: number;
    asset_type_id: number;
    template_name: string;
    template_description: string;
    asset_note: string;
    asset_height: number;
    asset_tags: Array<number>;

    is_deleted?: boolean;
    last_updated?: Moment;
    created_date: Moment;
}

export class AssetTemplate extends ImsObject {
    assetType: AssetType;

    assetTags: {
        [x: number]: Tag;
    };

    assetTemplateIntegrations: {
        [x: number]: AssetTemplateIntegration;
    };

    ipDevices: {
        [x: number]: IpDeviceTemplate;
    };

    assets: {
        [x: number]: Asset;
    };

    constructor(resource: IAssetTemplateResource = new AssetTemplateResource()) {
        super(IMS_TYPE__ASSET_TEMPLATE, resource);
        this.assetTags = {};
        this.assetTemplateIntegrations = {};
        this.ipDevices = {};
        this.assets = {};
    }

    get templateName(): string {
        return this.resource.template_name;
    }

    set templateName(templateName: string) {
        this.resource.template_name = templateName;
    }

    get initialTemplateName(): string {
        return this.resource.initial_template_name;
    }

    set initialTemplateName(templateName: string) {
        this.resource.initial_template_name = templateName;
    }

    get name(): string {
        return this.templateName;
    }

    get templateDescription(): string {
        return this.resource.template_description;
    }

    set templateDescription(description: string) {
        this.resource.template_description = description;
    }

    get assetTypeId(): number {
        return this.resource.asset_type_id;
    }

    set assetTypeId(typeId: number) {
        this.resource.asset_type_id = typeId;
    }

    get assetNote(): string {
        return this.resource.asset_note;
    }

    set assetNote(note: string) {
        this.resource.asset_note = note;
    }

    set siteId(siteId: number) {
        if (this.resource.site_id !== null) {
            this.resource.site_id = siteId;
        } else {
            throw new Error('Site ID already defined');
        }
    }

    get siteId(): number {
        return this.resource.site_id;
    }

    get assetHeight(): number {
        return this.resource.asset_height;
    }

    set assetHeight(height: number) {
        this.resource.asset_height = height;
    }

    get isDeleted(): boolean {
        return this.resource.is_deleted;
    }

    set isDeleted(isDeleted: boolean) {
        this.resource.is_deleted = isDeleted;
    }

    get lastUpdated(): Moment {
        return this.resource.last_updated;
    }

    set lastUpdated(lastUpdated: Moment) {
        this.resource.last_updated = lastUpdated;
    }

    get createdDate(): Moment {
        return this.resource.created_date;
    }

    /**
     * Need to use this if we want to update the tag array. DONT use this.tag
     * this.tag is used by collections in order to populate the asset ims object.
     * setTagArray is used by clients of Asset to update the underlying resource
     * object AND it will also update the tag dictionary
     * @param tagArray
     */
    setTagArray(tagArray: Array<Tag>) {
        const tagIdArray = [];
        const tagLookupById = {};
        _.forEach(tagArray, (tag) => {
            tagIdArray.push(tag.id);
            tagLookupById[tag.id] = tag;
        });
        this.resource.asset_tags = tagIdArray;
        this.assetTags = tagLookupById;
    }

    getTagArray(): Array<Tag> {
        return _.reduce(
            this.assetTags,
            (result, tag) => {
                result.push(tag);
                return result;
            },
            [],
        );
    }

    /**
     * Some weak validation for an asset template.
     *
     * @param {AssetTemplate} assetTemplate
     * @returns {bool} True if valid
     */
    static isValid(assetTemplate: AssetTemplate): boolean {
        if (!assetTemplate.assetTypeId) {
            return false;
        }
        return isValidRequiredString(assetTemplate.templateName);
    }
}
